import { polygonToPath } from '@/components/dialogs/assemblyDrawing/drawingLayout';
import { useAssemblyThumbnail } from '@/hooks/useAssemblyThumbnail';
import type { DrawingBody, DrawingExtents } from '@/types/assemblyDrawing.types';
import { PencilRuler } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The box a thumbnail is drawn in, in viewBox units. The CSS size only scales it, so the
 * same component serves the 30x42 badge in the tools table and a larger preview.
 */
const VIEW_WIDTH = 40;
const VIEW_HEIGHT = 56;

/** Breathing room, so a silhouette never touches the edge of its box. */
const VIEW_PADDING = 2;

/** Outline weight in viewBox units, kept a hairline at every rendered size. */
const OUTLINE_WIDTH = 0.5;

/**
 * A higher-contrast cousin of the drawing sheet's palette (`DRAWING_COLORS`).
 *
 * At thirty pixels wide the sheet's muted slate-on-slate fills collapse into one grey
 * blob, so the four bodies are pulled further apart: the clamping end stays slate, the
 * tool goes amber, and the cutting part is the one saturated accent — which is also the
 * part the eye is looking for when scanning a column of tools.
 */
const THUMBNAIL_COLORS = {
  holder: '#94a3b8',
  extension: '#cbd5e1',
  tool: '#fcd34d',
  cutting: '#f59e0b',
  outline: '#475569',
};

/** Where the drawing's bounding box lands inside the thumbnail box, and at what scale. */
function fitToBox(extents: DrawingExtents): { transform: string; scale: number } {
  const width = Math.max(extents.x_max - extents.x_min, Number.EPSILON);
  const height = Math.max(extents.z_max - extents.z_min, Number.EPSILON);
  const scale = Math.min((VIEW_WIDTH - 2 * VIEW_PADDING) / width, (VIEW_HEIGHT - 2 * VIEW_PADDING) / height);
  const centerX = (extents.x_min + extents.x_max) / 2;
  const centerZ = (extents.z_min + extents.z_max) / 2;
  // `polygonToPath` already flips z to screen y, so the path's own centre is (x, -z) and
  // the second translate moves *up* by centerZ rather than down.
  const transform = `translate(${VIEW_WIDTH / 2} ${VIEW_HEIGHT / 2}) scale(${scale}) translate(${-centerX} ${centerZ})`;
  return { transform, scale };
}

interface BodyOutlineProps {
  body: DrawingBody | null | undefined;
  fill: string;
  strokeWidth: number;
}

/** One filled body; absent bodies (no extension, no cutting part) draw nothing. */
const BodyOutline: FC<BodyOutlineProps> = ({ body, fill, strokeWidth }) => {
  if (!body) return null;
  return (
    <path
      d={polygonToPath(body.polygon)}
      fill={fill}
      stroke={THUMBNAIL_COLORS.outline}
      strokeWidth={strokeWidth}
      strokeLinejoin="round"
    />
  );
};

interface AssemblyThumbnailProps {
  /** Assembly to draw, or null when the tool has none. */
  assemblyId: string | null;
  /** NC number of that assembly, used to name the drawing. */
  ncNumber: number | null;
  /** Further assemblies of the same tool, shown as a "+N" badge. */
  extraCount?: number;
  /** Rendered size; the viewBox is fixed, so this only scales the silhouette. */
  className?: string;
}

/**
 * Icon-sized silhouette of one tool assembly, drawn from the same data as the drawing
 * dialog but stripped to the bodies — no dimensions, no leaders, no labels.
 *
 * It loads its own sheet through {@link useAssemblyThumbnail}, which caches across pages,
 * so a table row only has to say which assembly it wants. Tools with no assembly, and
 * assemblies the backend cannot draw, both fall back to a muted glyph rather than a gap.
 */
export const AssemblyThumbnail: FC<AssemblyThumbnailProps> = ({
  assemblyId,
  ncNumber,
  extraCount = 0,
  className = 'h-14 w-10',
}) => {
  const { t } = useTranslation();
  const state = useAssemblyThumbnail(assemblyId);

  const label =
    ncNumber != null
      ? t('toolManagement.thumbnailAlt', { ncNumber })
      : t('toolManagement.thumbnailAltWithoutNcNumber');

  const renderInner = () => {
    if (state.status === 'loading') {
      return <span className="block h-full w-full animate-pulse rounded bg-gray-100" aria-hidden="true" />;
    }

    if (state.status !== 'ready') {
      return (
        <span className="flex h-full w-full items-center justify-center rounded border border-dashed border-gray-200 text-gray-300">
          <PencilRuler size={13} aria-hidden="true" />
        </span>
      );
    }

    const { transform, scale } = fitToBox(state.spec.extents);
    // Divided by the group's own scale, so the stroke lands at OUTLINE_WIDTH viewBox units.
    const strokeWidth = OUTLINE_WIDTH / scale;

    return (
      <svg viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`} className="block h-full w-full" role="img" aria-label={label}>
        <title>{label}</title>
        {/* Painted in the order that lets the holder occlude the shank it swallows. */}
        <g transform={transform}>
          <BodyOutline body={state.spec.bodies.tool} fill={THUMBNAIL_COLORS.tool} strokeWidth={strokeWidth} />
          <BodyOutline body={state.spec.bodies.cutting} fill={THUMBNAIL_COLORS.cutting} strokeWidth={strokeWidth} />
          <BodyOutline body={state.spec.bodies.extension} fill={THUMBNAIL_COLORS.extension} strokeWidth={strokeWidth} />
          <BodyOutline body={state.spec.bodies.holder} fill={THUMBNAIL_COLORS.holder} strokeWidth={strokeWidth} />
        </g>
      </svg>
    );
  };

  const stateTitle = () => {
    if (state.status === 'empty') return t('toolManagement.thumbnailNoAssembly');
    if (state.status === 'unavailable') return t('toolManagement.thumbnailUnavailable');
    if (state.status === 'ready') return label;
    return undefined;
  };

  return (
    <span className={`relative block shrink-0 ${className}`} title={stateTitle()}>
      {renderInner()}
      {/* Bottom right: the silhouette narrows to the tool tip there, and the badge stays
          inside the box so a tight table row cannot clip it. */}
      {extraCount > 0 ? (
        <span
          className="absolute bottom-0 right-0 rounded-full bg-gray-600 px-1 text-xs font-semibold leading-4 text-white"
          title={t('toolManagement.thumbnailMoreAssemblies', { count: extraCount })}
        >
          {`+${extraCount}`}
        </span>
      ) : null}
    </span>
  );
};
